import { getPooledConnection } from "./OfflineStore";
import OfflineErrors from "./OfflineErrors";

export class Cache {
	static #localInstance;
	static #sessionInstance
	#store;

	/**
	 *
	 * @returns {Cache}
	 */
	static Local() {
		if (this.#localInstance) return this.#localInstance;

		this.#localInstance = new Cache();
		this.#localInstance.#store = localStorage;
		return this.#localInstance;
	}

	/**
	 *
	 * @returns {Cache}
	 */
	static Session() {
		if (this.#sessionInstance) return this.#sessionInstance;

		this.#sessionInstance = new Cache();
		this.#sessionInstance.#store = sessionStorage;
		return this.#sessionInstance;
	}

	set(key, value) {
		if (!key) throw new Error("Key required for Cache.Set()");
		let valueToSave = value;
		if (value instanceof Map) {
			valueToSave = Object.fromEntries(value);
		}

		valueToSave != null
			? this.#store.setItem(key, typeof value === "string" ? value : JSON.stringify(valueToSave))
			: this.#store.removeItem(key);
		return value;
	}

	get(key) {
		const item = this.#store.getItem(key);
		if (!item) return;
		try {
			return JSON.parse(item);
		}
		catch (e) {
			return item;
		}
	}
}

export class DB {
	static #DEFAULT_TTL = 21_600;  // 6 hours

	static Make_Name(id, prefix = undefined) {
		const dbPrefix = prefix ?? "catalog";
		return `${dbPrefix}_${id}`;
	}

	/**
	 * @param {String} dbName
	 * @param {string|string[]} key
	 * @param {*} value
	 * @param {int} ttl_seconds Defaults to 6 hours
	 */
	static async Set(dbName, key, value, ttl_seconds = undefined) {
		if (!dbName) {
			console.error("Invalid DB name", dbName);
			return;
		}

		let db;
		try {
			db = await getPooledConnection(dbName);
		} catch (e) {
			OfflineErrors.log("IDB_open_failed", e, { dbName, method: "DB.Set" });
			return;
		}
		if (!db) {
			return;
		}

		const tx = db.transaction(["cache"], "readwrite");
		const store = tx.objectStore("cache");

		return new Promise((resolve, reject) => {
			if (value == null) {
				const getReq = store.get(key);
				getReq.onsuccess = () => {
					if (getReq.result) store.delete(key);
				};
			} else {
				const expires = Date.now() + ((ttl_seconds ?? this.#DEFAULT_TTL) * 1_000);
				store.put({ key, expires, value });
			}

			tx.oncomplete = () => resolve(value);
			tx.onerror = (e) => {
				e.preventDefault();
				const err = e.target?.error;
				if (err instanceof DOMException && err.name === "QuotaExceededError") {
					console.warn("[DB] QuotaExceededError in Set — write skipped", { key });
					resolve(value);
				} else {
					console.warn("[DB] Transaction error in Set:", err);
					reject(err);
				}
			};
			tx.onabort = (e) => {
				e.preventDefault();
				const err = e.target?.error;
				if (err instanceof DOMException && err.name === "QuotaExceededError") {
					console.warn("[DB] QuotaExceededError (abort) in Set — write skipped", { key });
					resolve(value);
				} else {
					console.warn("[DB] Transaction aborted in Set");
					reject(new Error("Transaction aborted"));
				}
			};
		});
	}

	/**
	 * @param {string} dbName
	 * @param {string|string[]} key
	 */
	static async Get(dbName, key) {
		if (!dbName) {
			console.warn("[DB] Get called without dbName");
			return undefined;
		}

		let db;
		try {
			db = await getPooledConnection(dbName);
		} catch (e) {
			OfflineErrors.log("IDB_open_failed", e, { dbName, method: "DB.Get" });
			return undefined;
		}
		if (!db) {
			return undefined;
		}

		const tx = db.transaction(["cache"], "readonly");
		const store = tx.objectStore("cache");

		return new Promise((resolve, reject) => {
			const request = store.get(key);
			request.onsuccess = () => {
				const { key: recordKey, value, expires } = request.result ?? {};
				if (expires && expires <= Date.now()) {
					// Expired — delete in a separate transaction (don't block the read)
					try {
						const delTx = db.transaction(["cache"], "readwrite");
						delTx.objectStore("cache").delete(recordKey);
					} catch (_) {
						// Non-fatal: cleanup will happen on next access
					}
					return resolve();
				}
				resolve(value);
			};
			request.onerror = (event) => {
				event.preventDefault();
				reject(event.target.error);
			};
		});
	}

	static async Remove(dbName) {
		return new Promise((resolve) => {
			const request = window.indexedDB.deleteDatabase(dbName);
			request.onsuccess = () => resolve();
			request.onerror = () => resolve();
			request.onblocked = () => resolve();
		});
	}

	static async RemoveAll() {
		const dbs = await window.indexedDB.databases();
		await Promise.allSettled(dbs.map( db => this.Remove(db.name)));
	}
}
